import path from 'node:path';
import { getAutogeneratedBanner } from '../banners';
import {
  assertNoFieldNameCollisions,
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  getEnumMemberKind,
  getFieldDocLines,
  getSensitiveKeys,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

function getGoCoercedTypeString(coerced: CoercedType): string {
  if (coerced === 'int') return 'int64';
  if (coerced === 'number') return 'float64';
  if (coerced === 'boolean') return 'bool';
  if (coerced === 'object') return 'map[string]any';
  // the blob value is a real JSON array/object, so slices/maps unmarshal natively
  if (typeof coerced === 'object' && 'arrayOf' in coerced) {
    return `[]${getGoCoercedTypeString(coerced.arrayOf)}`;
  }
  if (typeof coerced === 'object' && 'recordOf' in coerced) {
    return `map[string]${coerced.recordOf.values ? getGoCoercedTypeString(coerced.recordOf.values) : 'any'}`;
  }
  // Go has no literal-union types, so an enum field gets its members' scalar type — the blob
  // carries the *coerced* value, so a numeric enum must be numeric or Unmarshal fails at runtime
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    const kind = getEnumMemberKind(coerced.enum);
    if (kind === 'int') return 'int64';
    if (kind === 'number') return 'float64';
    if (kind === 'boolean') return 'bool';
    if (kind === 'mixed') return 'any';
  }
  return 'string';
}

function snakeCaseToPascalCase(key: string): string {
  return key.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('');
}

// an exported Go struct field must start with an uppercase letter — a key like `_2FA_SECRET`
// (identifier-safe, but Pascal-cases to the digit-leading `2faSecret`) or a bare `_` can't be one
const GO_EXPORTED_FIELD_NAME = /^[A-Z][A-Za-z0-9]*$/;

// maps and `any` are already nullable in Go (nil means "absent"), so they don't get a pointer
function goFieldType(baseType: string, isRequired: boolean): string {
  // maps/slices are already nilable — no pointer wrapping needed for optionals
  if (isRequired || baseType.startsWith('map[') || baseType.startsWith('[]') || baseType === 'any') return baseType;
  return `*${baseType}`;
}

function fieldDocComment(field: ResolvedFieldType): Array<string> {
  return getFieldDocLines(field).map((line) => `\t// ${line}`);
}

function formatGoStruct(
  structName: string,
  doc: string,
  fields: Array<ResolvedFieldType>,
  getType: (field: ResolvedFieldType) => string,
): Array<string> {
  const lines = [
    '',
    `// ${structName} is ${doc}`,
    `type ${structName} struct {`,
  ];
  // gofmt aligns runs of consecutive field lines and breaks a run on any doc-comment line. Mirror
  // that grouping (a field with docs starts a new group) and pad names within each group so the
  // emitted source is already gofmt-clean and won't produce a dirty diff on save.
  const groups: Array<Array<ResolvedFieldType>> = [];
  for (const field of fields) {
    if (!groups.length || fieldDocComment(field).length) groups.push([]);
    groups[groups.length - 1].push(field);
  }
  for (const group of groups) {
    const width = Math.max(...group.map((f) => snakeCaseToPascalCase(f.key).length));
    for (const field of group) {
      lines.push(...fieldDocComment(field));
      lines.push(`\t${snakeCaseToPascalCase(field.key).padEnd(width)} ${getType(field)}`);
    }
  }
  lines.push('}');
  return lines;
}

// Go keywords are not identifiers, so they can't be package names (`package type` won't compile)
const GO_KEYWORDS = new Set([
  'break',
  'case',
  'chan',
  'const',
  'continue',
  'default',
  'defer',
  'else',
  'fallthrough',
  'for',
  'func',
  'go',
  'goto',
  'if',
  'import',
  'interface',
  'map',
  'package',
  'range',
  'return',
  'select',
  'struct',
  'switch',
  'type',
  'var',
]);

/**
 * Go requires the package name to match the output directory (`path=config/env.go` → `package config`).
 * Derive it from the dir, sanitized to a legal identifier, with an explicit `package=` override winning.
 */
export function resolveGoPackageName(outputPath: string, override?: unknown): string {
  const sanitize = (raw: string): string => {
    let name = raw.toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (name && /^[0-9]/.test(name)) name = `_${name}`;
    if (GO_KEYWORDS.has(name)) name = `${name}_`;
    return name;
  };
  if (override !== undefined) {
    const sanitized = typeof override === 'string' ? sanitize(override) : '';
    // an explicit override that sanitizes to nothing is a user error, not something to silently drop
    if (!sanitized) {
      throw new Error(`@generateGoEnv - \`package\` must contain at least one letter, digit, or underscore (got ${JSON.stringify(override)})`);
    }
    return sanitized;
  }
  return sanitize(path.basename(path.dirname(outputPath))) || 'env';
}

export function generateGoEnvSrc(fields: Array<ResolvedFieldType>, opts?: { packageName?: string }): string {
  // struct/loader cover representable keys; skipped keys stay as raw env vars (os.Getenv)
  const { safe, skipped } = partitionRepresentableKeys(
    fields,
    (k) => !GO_EXPORTED_FIELD_NAME.test(snakeCaseToPascalCase(k)),
  );
  assertNoFieldNameCollisions(safe, snakeCaseToPascalCase, 'Go');
  const packageName = opts?.packageName || 'env';
  // SensitiveKeys covers every sensitive key (incl. skipped ones) so redaction doesn't miss them
  const sensitiveKeys = getSensitiveKeys(fields);

  const src = [
    getAutogeneratedBanner('//'),
    '',
    `// Package ${packageName} is generated by varlock; Load() parses the __VARLOCK_ENV blob into typed values.`,
    `package ${packageName}`,
    '',
    'import (',
    '\t"encoding/json"',
    '\t"fmt"',
    '\t"os"',
    '\t"strings"',
    ')',
    ...(skipped.length ? ['', ...skippedKeysComment(skipped, '//')] : []),
    ...formatGoStruct(
      'Env',
      'the coerced env schema (matches varlock coercion).',
      safe,
      (field) => goFieldType(getGoCoercedTypeString(field.coerced), field.isRequired),
    ),
    '',
    '// SensitiveKeys marks which keys hold sensitive values — use to build your own redaction / leak-scanning.',
    `var SensitiveKeys = map[string]bool{${sensitiveKeys.map((k) => `${JSON.stringify(k)}: true`).join(', ')}}`,
    '',
    '// Load parses the __VARLOCK_ENV blob (injected by `varlock run`) into a typed, coerced Env.',
    'func Load() (Env, error) {',
    '\tblob := os.Getenv("__VARLOCK_ENV")',
    '\tif blob == "" {',
    `\t\treturn Env{}, fmt.Errorf("varlock: %s", ${JSON.stringify(BLOB_MISSING_MSG)})`,
    '\t}',
    `\tif strings.HasPrefix(blob, ${JSON.stringify(ENCRYPTED_BLOB_PREFIX)}) {`,
    `\t\treturn Env{}, fmt.Errorf("varlock: %s", ${JSON.stringify(BLOB_ENCRYPTED_MSG)})`,
    '\t}',
    '\tvar graph struct {',
    '\t\tConfig map[string]struct {',
    '\t\t\tValue json.RawMessage `json:"value"`',
    '\t\t} `json:"config"`',
    '\t}',
    '\tif err := json.Unmarshal([]byte(blob), &graph); err != nil {',
    '\t\treturn Env{}, fmt.Errorf("varlock: failed to parse __VARLOCK_ENV: %w", err)',
    '\t}',
    '\tvar e Env',
    '\tfields := []struct {',
    '\t\tkey      string',
    '\t\tdst      any',
    '\t\trequired bool',
    '\t}{',
    ...safe.map((f) => `\t\t{${JSON.stringify(f.key)}, &e.${snakeCaseToPascalCase(f.key)}, ${f.isRequired ? 'true' : 'false'}},`),
    '\t}',
    '\tvar missing []string',
    '\tfor _, f := range fields {',
    '\t\tc, ok := graph.Config[f.key]',
    '\t\t// unset optional keys carry no "value" (nil RawMessage); a missing required key is an error',
    '\t\tif !ok || len(c.Value) == 0 {',
    '\t\t\tif f.required {',
    '\t\t\t\tmissing = append(missing, f.key)',
    '\t\t\t}',
    '\t\t\tcontinue',
    '\t\t}',
    '\t\tif err := json.Unmarshal(c.Value, f.dst); err != nil {',
    '\t\t\treturn Env{}, fmt.Errorf("varlock: failed to parse %s: %w", f.key, err)',
    '\t\t}',
    '\t}',
    '\tif len(missing) > 0 {',
    '\t\treturn Env{}, fmt.Errorf("varlock: missing required keys in __VARLOCK_ENV: %s", strings.Join(missing, ", "))',
    '\t}',
    '\treturn e, nil',
    '}',
    '',
  ];

  return src.join('\n');
}
